import { differenceInHours, isSameDay } from "date-fns";
import { useState, useMemo, useCallback, FC, useEffect } from "react";
import useStore from "../hooks/useStore";
import {
  ScheduleResult,
  getWebinarStartDateAndTime,
  addToCart,
  registerAnalyticsEvent,
} from "../util/webinars";
import WebinarOptionsDropdown from "./WebinarOptionsDropdown";

interface WebinarOptionsRowProps {
  withBorder?: boolean;
}

const WebinarOptionsRow: FC<WebinarOptionsRowProps> = ({ withBorder }) => {
  const activeWebinar = useStore((state) => state.activeWebinar);
  const dayOfActiveWebinar = useStore((state) => state.dayOfActiveWebinar);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const options = activeWebinar.ScheduleResults;

  const credits = useMemo(
    () => options[selectedIndex].Credits,
    [options, selectedIndex]
  );

  const price = useMemo(
    () => options[selectedIndex].Price,
    [options, selectedIndex]
  );

  const selectedOccuranceDuration = useMemo(() => {
    const startTime = new Date(options[selectedIndex].StartDateString);
    const endTime = new Date(options[selectedIndex].EndDateString);

    return differenceInHours(endTime, startTime);
  }, [options, selectedIndex]);

  const handleAddToCart = useCallback(() => {
    const selectedItem: ScheduleResult = options[selectedIndex];

    const [ProductDate, ProductTime] = getWebinarStartDateAndTime(
      selectedItem.StartDateString,
      selectedItem.EndDateString,
      selectedItem.TimeZone
    );

    const payload = {
      CourseID: activeWebinar.ID, //webinar.ID,
      ProductID: selectedItem.LiveEventProductId!,
      ProductCodes: activeWebinar.ScheduleResults.length,
      Acronym: activeWebinar.Acronym,
      Title: activeWebinar.CourseTitle,
      UnitPrice: parseFloat(selectedItem.Price.slice(1)),
      IsMobile: activeWebinar.isMobileCourse,
      ProductCredits: activeWebinar.TotalCredits,
      ProductDate,
      ProductTime,
    };

    registerAnalyticsEvent("TSG_cal_addtocart_popup");

    addToCart(payload);
  }, [
    activeWebinar.Acronym,
    activeWebinar.CourseTitle,
    activeWebinar.ID,
    activeWebinar.ScheduleResults.length,
    activeWebinar.TotalCredits,
    activeWebinar.isMobileCourse,
    options,
    selectedIndex,
  ]);

  useEffect(() => {
    if (!dayOfActiveWebinar) return undefined;

    for (const [idx, option] of options.entries()) {
      const webinarStartDateTime = new Date(option.StartDateString);

      if (isSameDay(webinarStartDateTime, dayOfActiveWebinar)) {
        setSelectedIndex(idx);
      }
    }
  }, [options, dayOfActiveWebinar]);

  return (
    <>
      <div
        className={`${
          withBorder ? "tw-border-y" : ""
        } tw-py-4 tw-text-tr-date/75 tw-flex tw-flex-col tw-space-y-6 md:tw-space-y-0 md:tw-flex-row tw-items-center tw-justify-between tw-relative`}
      >
        <WebinarOptionsDropdown
          selectedIndex={selectedIndex}
          value={options[selectedIndex]}
          options={options}
          onChange={setSelectedIndex}
        />
        <div className="tw-flex tw-items-center tw-px-4 md:tw-px-0 tw-w-full md:tw-w-auto tw-justify-between md:tw-justify-around tw-flex-1 ">
          <div className="tw-text-xl tw-text-tr-green tw-font-medium">
            {credits} {credits === 1 ? "Credit" : "Credits"}
          </div>
          <div className="tw-text-2xl tw-font-medium tw-text-tr-black">
            {price}
          </div>
        </div>

        <button
          className="tw-w-full md:tw-w-auto tw-bg-tr tw-rounded-md tw-text-white tw-font-medium tw-text-xl tw-px-10 tw-py-3 hover:tw-shadow-md"
          onClick={handleAddToCart}
        >
          Add to cart
        </button>
      </div>
      <div className="tw-flex tw-justify-end tw-mt-4">
        <p>
          <i className="tw-text-tr-date">
            This webinar is included with the subscription(s): {}
          </i>
          {selectedOccuranceDuration <= 4 && (
            <strong className="tw-ml-6 tw-text-tr-dark-blue">
              <a
                href="/cpe-packages/premier-cpe"
                target="_blank"
                rel="noreferrer"
              >
                Premier CPE Package
              </a>
            </strong>
          )}
          <strong className="tw-ml-6 tw-text-tr-dark-blue">
            <a
              href="/cpe-packages/premier-plus-cpe"
              target="_blank"
              rel="noreferrer"
            >
              Premier Plus CPE Package
            </a>
          </strong>
        </p>
      </div>
    </>
  );
};

export default WebinarOptionsRow;
